import { useMemo, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, LabelList
} from "recharts";
import { exportAsImage } from "../../utils/exportAsImage";

// months order
const monthsOrder = ["Feb","Apr","Jun","Aug","Oct","Dec"];

// Big distinct color palette
const basePalette = [
  "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728",
  "#9467bd", "#8c564b", "#e377c2", "#7f7f7f",
  "#bcbd22", "#17becf", "#ff1493", "#00ced1",
  "#ffa500", "#32cd32", "#ff6347", "#4682b4"
];

const highlightColor = "#FFF8DC"; // light yellow highlight for new age group
const improvedColor = "green";
const droppedColor = "red";

// Utility: generate shade of a color
function shadeColor(color, percent) {
  let num = parseInt(color.replace("#",""),16),
      amt = Math.round(2.55 * percent),
      R = (num >> 16) + amt,
      G = (num >> 8 & 0x00FF) + amt,
      B = (num & 0x0000FF) + amt;
  return "#" + (
    0x1000000 +
    (R<255? (R<0?0:R):255)*0x10000 +
    (G<255? (G<0?0:G):255)*0x100 +
    (B<255? (B<0?0:B):255)
  ).toString(16).slice(1);
}

export default function RankingProgression({ ajssRankings, selectedYear }) {
  const { chartData, segments, playerNames, timeline, playerTable } = useMemo(() => {
    if (!ajssRankings?.length) return { chartData: [], segments: [], playerNames: [], timeline: [], playerTable: [] };

    const sorted = [...ajssRankings].sort((a, b) => {
      const yearDiff = a.year - b.year;
      if (yearDiff !== 0) return yearDiff;
      return monthsOrder.indexOf(a.month) - monthsOrder.indexOf(b.month);
    });

    const timeline = Array.from(new Set(sorted.map(r => r.month)));
    const players = {};

    sorted.forEach(r => {
      if (!players[r.playerId]) players[r.playerId] = { name: r.playerName, records: [] };
      players[r.playerId].records.push(r);
    });

    const chartData = timeline.map(label => ({ date: label }));
    const segments = [];
    let playerIndex = 0;

    Object.entries(players).forEach(([playerId, p]) => {
      const baseColor = basePalette[playerIndex % basePalette.length];
      playerIndex++;

      let currentAgeGroup = null;
      let segIndex = -1;

      p.records.forEach(r => {
        if (r.ageGroup !== currentAgeGroup) {
          segIndex++;
          currentAgeGroup = r.ageGroup;
        }
        const segKey = `${playerId}_seg${segIndex}`;
        const row = chartData.find(d => d.date === r.month);
        if (row) row[segKey] = r.rank;

        segments.push({
          segKey,
          playerName: p.name,
          ageGroup: r.ageGroup,
          color: shadeColor(baseColor, segIndex * 15),
        });
      });
    });

    const uniqueSegments = Object.values(
      segments.reduce((acc, seg) => {
        acc[seg.segKey] = seg;
        return acc;
      }, {})
    );

    const playerNames = Object.values(players)
      .map(p => p.name)
      .sort((a, b) => a.localeCompare(b));

    // Build enhanced table
    const playerTable = playerNames.map((name, idx) => {
      const playerEntry = Object.values(players).find(p => p.name === name);
      const baseColor = basePalette[idx % basePalette.length];

      // Handle players with no records at all
      if (!playerEntry || playerEntry.records.length === 0) {
        const row = { index: idx + 1, name, ageGroup: "-", change: "-", baseColor, highlightStart: null };
        timeline.forEach(m => (row[m] = "–"));
        return row;
      }

      // Extract age groups in order
      const ageGroups = [...new Set(playerEntry.records.map(r => r.ageGroup))];
      const ageGroupDisplay = ageGroups.join(" / ");

      const row = { index: idx + 1, name, ageGroup: ageGroupDisplay, baseColor };

      timeline.forEach(month => {
        const match = playerEntry.records.find(r => r.month === month);
        if (match) {
          row[month] = match.rank ?? "–";
          row[`${month}_ageGroup`] = match.ageGroup;
        } else {
          row[month] = "–";
        }
      });

      // Calculate highlight start month (when group changes)
      let highlightStart = null;
      if (ageGroups.length > 1) {
        for (let i = 1; i < timeline.length; i++) {
          const prevGroup = row[`${timeline[i - 1]}_ageGroup`];
          const currGroup = row[`${timeline[i]}_ageGroup`];
          if (prevGroup && currGroup && prevGroup !== currGroup) {
            highlightStart = timeline[i];
            break;
          }
        }
      }

      // Calculate ranking changes per group
      let changeStrings = [];
      ageGroups.forEach((group) => {
        const ranks = playerEntry.records
          .filter(r => r.ageGroup === group && typeof r.rank === "number")
          .map(r => r.rank);
        if (ranks.length >= 2) {
          const diff = ranks[0] - ranks[ranks.length - 1];
          if (diff > 0) changeStrings.push(`▲ ${diff}`);
          else if (diff < 0) changeStrings.push(`▼ ${Math.abs(diff)}`);
          else changeStrings.push("–");
        } else changeStrings.push("–");
      });

      row.change = changeStrings.join(" / ");
      row.highlightStart = highlightStart;
      return row;
    });

    return { chartData, segments: uniqueSegments, playerNames, timeline, playerTable };
  }, [ajssRankings]);

  const [visiblePlayers, setVisiblePlayers] = useState(() =>
    Object.fromEntries(playerNames.map(name => [name, true]))
  );

  const togglePlayer = (name) =>
    setVisiblePlayers((prev) => ({ ...prev, [name]: !prev[name] }));

  const getHighlightMonths = (row) => {
    if (!row.highlightStart) return [];
    const startIndex = row.highlightStart
      ? monthsOrder.indexOf(row.highlightStart)
      : -1;
    return startIndex >= 0
      ? monthsOrder.slice(startIndex)
      : [];
  };

  return (
    <div className="space-y-6">
      <div className="text-right">
        <button
          onClick={() => exportAsImage("ranking-progression-section", "ranking-progression")}
          className="px-3 py-1 bg-blue-500 text-white rounded"
        >
          Save as Image
        </button>
      </div>

      <div className="p-4 bg-white shadow rounded-xl" id="ranking-progression-section">
        <h1 className="mb-4">Asian Junior Ranking {selectedYear}</h1>

        {/* Player toggles */}
        <div className="flex flex-wrap gap-4 mb-4 no-export">
          {playerNames.map((name) => (
            <label key={name} className="flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={visiblePlayers[name]}
                onChange={() => togglePlayer(name)}
              />
              {name}
            </label>
          ))}
        </div>

        {/* Chart */}
        {chartData.length ? (
          <ResponsiveContainer width="100%" height={500}>
            <LineChart data={chartData}>
              <XAxis dataKey="date" />
              <YAxis
                reversed
                allowDecimals={false}
                label={{ value: "Ranking", angle: -90, position: "insideLeft", offset: 10 }}
              />
              <Tooltip />
              <Legend />
              {segments.map((seg) =>
                visiblePlayers[seg.playerName] ? (
                  <Line
                    key={seg.segKey}
                    type="monotone"
                    dataKey={seg.segKey}
                    stroke={seg.color}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    name={`${seg.playerName} (${seg.ageGroup})`}
                  >
                    <LabelList dataKey={seg.segKey} position="top" fontSize={12} fill={seg.color} />
                  </Line>
                ) : null
              )}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-gray-500">No data</p>
        )}

        {/* Ranking Table */}
        {playerTable.length > 0 && (
          <div className="mt-8 overflow-x-auto">
            <table className="min-w-[900px] w-full border text-sm text-center">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border px-3 py-2">#</th>
                  <th className="border px-3 py-2 text-left">Player Name</th>
                  <th className="border px-3 py-2 text-center">Age Group</th>
                  {timeline.map((month) => (
                    <th key={month} className="border px-3 py-2">{month}</th>
                  ))}
                  <th className="border px-3 py-2 text-center">Ranking Change</th>
                </tr>
              </thead>
              <tbody>
                {playerTable.map((row) => {
                  const highlightMonths = getHighlightMonths(row);
                  const highlightBg = highlightColor;

                  return (
                    <tr key={row.name}>
                      <td className="border px-3 py-2">{row.index}</td>
                      <td className="border px-3 py-2 text-left">{row.name}</td>
                      <td className="border px-3 py-2">
                        {row.ageGroup.split(" / ").map((g, i) => (
                          <span
                            key={i}
                            style={{
                              backgroundColor:
                                row.ageGroup.includes("/") && i === 1 ? highlightBg : "transparent",
                              padding: "2px 4px",
                              borderRadius: "4px",
                            }}
                          >
                            {g}
                            {row.ageGroup.includes("/") && i === 0 && " / "}
                          </span>
                        ))}
                      </td>

                      {timeline.map((month) => {
                        const bgColor = highlightMonths.includes(month)
                          ? highlightBg
                          : "transparent";
                        return (
                          <td
                            key={month}
                            className="border px-3 py-2"
                            style={{ backgroundColor: bgColor }}
                          >
                            {row[month]}
                          </td>
                        );
                      })}

                      <td className="border px-3 py-2">
                        {row.change.split(" / ").map((chg, i) => {
                          if (chg.includes("▲"))
                            return (
                              <span key={i} style={{ color: improvedColor }}>
                                {chg}{i === 0 && row.change.includes("/") ? " / " : ""}
                              </span>
                            );
                          else if (chg.includes("▼"))
                            return (
                              <span key={i} style={{ color: droppedColor }}>
                                {chg}{i === 0 && row.change.includes("/") ? " / " : ""}
                              </span>
                            );
                          return (
                            <span key={i}>
                              {chg}{i === 0 && row.change.includes("/") ? " / " : ""}
                            </span>
                          );
                        })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
